import React, { useEffect, useMemo, useState } from "react"
import { EventLog, useEventLogs } from "./event-log-store"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui-primitives/resizable"
import { Badge } from "@/components/ui-primitives/badge"
import { mergeClasses } from "@/helpers/styling"
import { Panel, useFloatingState } from "../panels/panel-registry"
import { ComponentNamespace, GLOBAL_NAMESPACE } from "@/redux/pubsub"
import { assertNever } from "@/utils/assert-never"

type IndexedLog = { event: EventLog; index: number }
type LogPart = {
  type: "item"
  log: IndexedLog
} | {
  type: "group"
  ns: string | null
  ctx: string | null
  logs: IndexedLog[]
}

export function EventLogs() {
  const floatingState = useFloatingState()
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
  const eventLogs = useEventLogs()

  const availableNamespaces = useMemo(() => {
    const ns = new Set<ComponentNamespace>()
    for (const event of eventLogs) {
      const key = getEventNamespace(event)
      if (key !== null) ns.add(key)
    }
    return Array.from(ns)
  }, [eventLogs])

  const [selectedNamespaces, setSelectedNamespaces] = useState<Set<ComponentNamespace>>(() => new Set())

  useEffect(() => {
    setSelectedNamespaces((prev) => {
      const toAdd = availableNamespaces.filter((ns) => !prev.has(ns))
      if (!toAdd.length) return prev
      const next = new Set(prev)
      for (const ns of toAdd) next.add(ns)
      return next
    })
  }, [availableNamespaces])

  function toggleNamespace(key: ComponentNamespace) {
    setSelectedNamespaces((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const filteredLogs = useMemo<IndexedLog[]>(() => {
    return eventLogs
      .map((event, index) => ({ event, index }))
      .filter(({ event }) => {
        const key = getEventNamespace(event)
        if (key === null) return true
        return selectedNamespaces.has(key)
      })
  }, [eventLogs, selectedNamespaces])

  const logParts = useMemo(() => buildLogParts(filteredLogs), [filteredLogs])

  const showNamespaceBar = availableNamespaces.length > 1

  if (!eventLogs.length)
    return (
      <div className="self-center text-gray-700 text-center m-4">
        No wistor-events have happened, since activating edit-mode.
      </div>
    )

  const selected = selectedIdx != null ? eventLogs[selectedIdx] : null

  const logList = (
    <div className="flex flex-col p-1">
      {logParts.map((part) => {
        switch (part.type) {
          case "item":
            return (
              <EventLogRow
                key={part.log.index}
                item={part.log.event}
                selected={part.log.index === selectedIdx}
                onSelect={() => setSelectedIdx(part.log.index)}
              />
            )
          case "group":
            return (
              <div key={`grp-${part.ns ?? ""}-${part.ctx ?? ""}`} className="ml-0.5 my-1">
                <GroupHeader ns={part.ns} ctx={part.ctx} firstEventType={part.logs[0].event.type} />
                {part.logs.map(({ event, index }) => (
                  <EventLogRow
                    key={index}
                    item={event}
                    selected={index === selectedIdx}
                    onSelect={() => setSelectedIdx(index)}
                    inGroup
                  />
                ))}
              </div>
            )
          default:
            return assertNever(part)
        }
      })}
    </div>
  )

  const selectedLog = selected && <SelectedEventLog event={selected} />

  if (floatingState === "docked") {
    return (
      <>
        {showNamespaceBar && (
          <NamespaceBar namespaces={availableNamespaces} selected={selectedNamespaces} onToggle={toggleNamespace} />
        )}
        {logList}
        {selectedIdx != null && (
          <Panel
            id="event-logs-aux"
            role="auxiliary"
            title="Selected log"
            onClose={() => setSelectedIdx(null)}
            defaultVisible
            defaultFloatingDimensions={{ width: 800, height: 600 }}
          >
            <div className="p-2">{selectedLog}</div>
          </Panel>
        )}
      </>
    )
  } else {
    return (
      <div className="min-h-0 h-full flex flex-col">
        {showNamespaceBar && (
          <NamespaceBar namespaces={availableNamespaces} selected={selectedNamespaces} onToggle={toggleNamespace} />
        )}
        <div className="min-h-0 h-full">
          <ResizablePanelGroup orientation="horizontal" className="max-h-full flex-grow h-full p-1">
            <ResizablePanel className="overflow-auto h-full flex flex-col p-1">{logList}</ResizablePanel>
            <ResizableHandle withHandle className="mx-1" />
            <ResizablePanel className="overflow-auto h-full flex flex-col p-2 text-sm">{selectedLog}</ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>
    )
  }
}

function NamespaceBar(props: {
  namespaces: ComponentNamespace[]
  selected: Set<ComponentNamespace>
  onToggle: (key: ComponentNamespace) => void
}) {
  return (
    <div className="flex flex-wrap gap-1 px-2 py-1.5 border-b flex-shrink-0">
      {props.namespaces.map((ns) => (
        <button
          key={String(ns)}
          onClick={() => props.onToggle(ns)}
          className={mergeClasses("text-[10.5px] px-2 py-0.5 rounded-full border transition-colors", {
            "bg-sky-100 border-sky-300 text-sky-700": props.selected.has(ns),
            "bg-white border-stone-300 text-stone-400": !props.selected.has(ns),
          })}
        >
          {ns === GLOBAL_NAMESPACE ? "global" : ns}
        </button>
      ))}
    </div>
  )
}

function GroupHeader(props: { ns: string | null; ctx: string | null; firstEventType: EventLog["type"] }) {
  const { ns, ctx } = props
  if (ctx) {
    return (
      <div className="border-l-2 border-amber-400 bg-amber-50 text-amber-800 px-2 py-0.5 text-[10px] font-medium flex items-center gap-1.5 overflow-hidden mb-px">
        {ns && <span className="flex-shrink-0 font-bold">{ns}</span>}
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{ctx}</span>
      </div>
    )
  }
  return (
    <div
      className={mergeClasses(
        "border-l-2 px-2 py-0.5 text-[10px] font-bold overflow-hidden text-ellipsis whitespace-nowrap mb-px",
        {
          "border-green-400 bg-green-50 text-green-800": props.firstEventType === "sparql-request-event",
          "border-green-400 bg-green-50 text-green-800 ": props.firstEventType === "construct-request-event",
          "border-yellow-400 bg-yellow-50 text-yellow-800": props.firstEventType === "app-variable-update-event",
          "border-purple-400 bg-purple-50 text-purple-800": props.firstEventType === "rule-execution-event",
          "border-slate-400 bg-slate-50 text-slate-700": props.firstEventType === "template-resolution-event",
        }
      )}
    >
      {ns}
    </div>
  )
}

function EventLogRow(props: { item: EventLog; selected: boolean; onSelect: () => void; inGroup?: boolean }) {
  const ns = getEventNamespace(props.item)
  const displayNs = props.inGroup ? null : (ns !== null && ns !== GLOBAL_NAMESPACE ? ns : null)

  return (
    <button
      onClick={() => props.onSelect()}
      className={mergeClasses(
        "w-full overflow-hidden flex flex-row text-left gap-1 text-xs border-y bg-gray-100 bg-opacity-50 hover:bg-opacity-75",
        {
          "bg-opacity-100 font-bold": props.selected,
          "bg-yellow-200": props.item.type === "app-variable-update-event",
          "bg-green-200": props.item.type === "sparql-request-event",
          "bg-green-200 ": props.item.type === "construct-request-event",
          "bg-purple-200": props.item.type === "rule-execution-event",
          "bg-slate-200": props.item.type === "template-resolution-event",
        }
      )}
    >
      <Badge
        className={mergeClasses("bg-gray-600", {
          "bg-yellow-600": props.item.type === "app-variable-update-event",
          "bg-green-600": props.item.type === "sparql-request-event",
          "bg-green-600 ": props.item.type === "construct-request-event",
          "bg-purple-600": props.item.type === "rule-execution-event",
          "bg-slate-600": props.item.type === "template-resolution-event",
        })}
      >
        {(() => {
          switch (props.item.type) {
            case "app-variable-update-event":
              return "VAR"
            case "sparql-request-event":
              return "SPARQL"
            case "construct-request-event":
              return "CONSTRUCT"
            case "rule-execution-event":
              return "RULE"
            case "template-resolution-event":
              return "TEMPLATE"
            default:
              return assertNever(props.item)
          }
        })()}
      </Badge>
      {displayNs && (
        <span
          className={mergeClasses("text-[9px] px-1.5 py-px rounded border flex-shrink-0 self-center", {
            "bg-yellow-100 border-yellow-300 text-yellow-700": props.item.type === "app-variable-update-event",
            "bg-green-100 border-green-300 text-green-700": props.item.type === "sparql-request-event",
            "bg-green-100 border-green-300 text-green-700 ": props.item.type === "construct-request-event",
            "bg-purple-100 border-purple-300 text-purple-700": props.item.type === "rule-execution-event",
            "bg-slate-100 border-slate-300 text-slate-600": props.item.type === "template-resolution-event",
          })}
        >
          {displayNs}
        </span>
      )}
      <span className="flex flex-grow items-center overflow-hidden text-ellipsis whitespace-nowrap">
        {(() => {
          switch (props.item.type) {
            case "app-variable-update-event":
              return (
                <>
                  {props.item.key} := {props.item.valueNew}
                </>
              )
            case "sparql-request-event":
            case "construct-request-event":
              return (
                <>
                  {props.item.source} {props.item.isRefetch && "[refetch]"}
                </>
              )
            case "rule-execution-event":
              return <>{props.item.tag}</>
            case "template-resolution-event":
              return <>{props.item.template}</>
            default:
              return assertNever(props.item)
          }
        })()}
      </span>
      <span className="flex items-center font-mono opacity-75 text-xs flex-shrink-0">[{props.item.timestamp}]</span>
    </button>
  )
}

function SelectedEventLog(props: { event: EventLog }) {
  switch (props.event.type) {
    case "app-variable-update-event":
      return (
        <>
          {props.event.ctx && (
            <div className="mb-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {props.event.ctx}
            </div>
          )}
          <Header typeLabel="app-variable update" timestamp={props.event.timestamp} />
          <Field label="variable identifier">
            <Code>{props.event.key}</Code>
          </Field>
          <Field label="old value">
            <Code>{props.event.valueOld}</Code>
          </Field>
          <Field label="new value">
            <Code>{props.event.valueNew}</Code>
          </Field>
        </>
      )

    case "sparql-request-event":
      return (
        <>
          <Header typeLabel="sparql request" timestamp={props.event.timestamp} />
          <Field label="request">
            <Code>{props.event.request}</Code>
          </Field>
          <Field label="response">
            <Code className="whitespace-pre-wrap">{props.event.response}</Code>
          </Field>
        </>
      )

    case "construct-request-event":
      return (
        <>
          <Header typeLabel="construct request" timestamp={props.event.timestamp} />
          <Field label="request">
            <Code>{props.event.request}</Code>
          </Field>
          <Field label="response">
            <Code className="whitespace-pre-wrap">{props.event.response}</Code>
          </Field>
        </>
      )

    case "rule-execution-event":
      return (
        <>
          <Header typeLabel="rule execution" timestamp={props.event.timestamp} />
          <Field label="tag">
            <Code>{props.event.tag}</Code>
          </Field>
          <Field label="parameters">
            <Code>{props.event.params}</Code>
          </Field>
        </>
      )

    case "template-resolution-event":
      return (
        <>
          <Header typeLabel="template resolution" timestamp={props.event.timestamp} />
          <Field label="template">
            <Code>{props.event.template}</Code>
          </Field>
          <Field label="resolutions">
            <Code>{props.event.partialResults}</Code>
          </Field>
          {props.event.endResult && (
            <Field label="result">
              <Code>{props.event.endResult}</Code>
            </Field>
          )}
        </>
      )

    default:
      return assertNever(props.event)
  }
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <span className="flex opacity-75">{props.label}</span>
      {props.children}
    </div>
  )
}

function Code(props: { children: React.ReactNode; className?: string }) {
  return (
    <pre className={mergeClasses("select-all bg-slate-100 p-1 rounded-sm font-mono", props.className)}>
      {props.children}
    </pre>
  )
}

function Header(props: { typeLabel: string; timestamp?: string }) {
  return (
    <div className="mb-3 flex flex-row justify-between">
      <div>
        <span className="opacity-75 mr-2">type</span>
        <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{props.typeLabel}</code>
      </div>
      {props.timestamp && (
        <div>
          <span className="opacity-75 mr-2">timestamp</span>
          <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{props.timestamp}</code>
        </div>
      )}
    </div>
  )
}

function getEventNamespace(event: EventLog): ComponentNamespace | null {
  switch (event.type) {
    case "app-variable-update-event": /* fallthrough */
    case "sparql-request-event": /* fallthrough */
    case "construct-request-event":
      return event.namespace ?? GLOBAL_NAMESPACE
    case "rule-execution-event": /* fallthrough */
    case "template-resolution-event":
      return null
    default:
      return assertNever(event)
  }
}

function getEventContext(event: EventLog): string | undefined {
  switch (event.type) {
    case "app-variable-update-event":
      return event.ctx
    case "sparql-request-event": /* fallthrough */
    case "construct-request-event": /* fallthrough */
    case "rule-execution-event": /* fallthrough */
    case "template-resolution-event":
      return undefined
    default:
      return assertNever(event)
  }
}

type GroupPart = Extract<LogPart, { type: "group" }>
function buildLogParts(logs: IndexedLog[]): LogPart[] {
  const parts: LogPart[] = []
  const groups = new Map<string, GroupPart>()

  for (const log of logs) {
    const ctx = getEventContext(log.event)
    const namespace = getEventNamespace(log.event)
    const nsKey = namespace != null && namespace !== GLOBAL_NAMESPACE ? namespace : null

    if (!nsKey && !ctx) {
      parts.push({ type: "item", log })
      continue
    }

    const key = `${nsKey ?? ""}::${ctx ?? ""}`
    if (!groups.has(key)) {
      const group = {
        type: "group",
        ns: nsKey,
        ctx: ctx ?? null,
        logs: []
      } satisfies GroupPart
      groups.set(key, group)
      parts.push(group)
    }
    groups.get(key)!.logs.push(log)
  }

  return parts
}
